import { FilterGroupType } from '../Components/ProductFiltering/types/FilterGroupType';
import {
  PRODUCT_FILTERING_RECEIVE,
  PRODUCT_FILTERING_UPDATE_FILTERS,
  PRODUCT_FILTERING_SET_SORTING,
} from '../constants';
import { get } from '../Services/http';
import {
  setIsLoadingProductListing,
  setProducts,
} from './ProductListing.action';

let abortController = null;

export const queryProductFiltering = (
  productFilteringState = null,
  isInitialLoad = false
) => async (dispatch, getState) => {
  try {
    productFilteringState ??= getState().productFiltering;
    const productListingState = getState().productListing;

    // Show skeleton cards before loading the new products
    if (!isInitialLoad) {
      dispatch(setIsLoadingProductListing(true));
      dispatch(
        setProducts(Array.from({ length: productListingState.pageSize }))
      );
    }

    // Cancel the last request
    if (abortController) {
      abortController.abort();
    }
    abortController = new AbortController();

    const queryString = getProductFilteringAsQueryString(productFilteringState);
    const response = await get(
      `/api/productfiltering?${queryString}`,
      abortController
    );

    if (response.ok) {
      const isJson = response.headers
        .get('content-type')
        ?.includes('application/json');
      if (isJson) {
        let data = await response.json();
        if (isInitialLoad) {
          delete data.productListing;
        }
        dispatch(receiveProductFiltering(data));
      }
    }
  } catch (err) {
    // TODO Error handling, not in MOX-86
    console.log(err);
  } finally {
    dispatch(setIsLoadingProductListing(false));
  }
};

export const receiveProductFiltering = (productFiltering) => ({
  type: PRODUCT_FILTERING_RECEIVE,
  payload: productFiltering,
});

export const getProductFilteringAsQueryString = (productFilteringState) => {
  const currentSearchParams = new URLSearchParams(location.search);
  const searchParams = new URLSearchParams();

  // Keep certain params from the current query
  const paramsToKeep = ['page', 'q', 'count'];
  paramsToKeep.forEach(
    (key) =>
      currentSearchParams.has(key) &&
      searchParams.append(key, currentSearchParams.get(key))
  );

  // Loop each filter, add each selected option, checkboxes
  productFilteringState.filters
    ?.filter(
      (f) =>
        f.selectedOptions.length > 0 &&
        f.filterGroupType != FilterGroupType.Range
    )
    .forEach((filter) =>
      filter.selectedOptions.forEach((selectedOption) => {
        searchParams.append(filter.id, selectedOption);
      })
    );

  // Range filters
  productFilteringState.filters
    ?.filter(
      (f) =>
        f.filterGroupType == FilterGroupType.Range &&
        f.selectedOptions.length == 2
    )
    .forEach((filter) => {
      searchParams.append(
        filter.id,
        `${filter.selectedOptions[0]}-${filter.selectedOptions[1]}`
      );
    });

  // Sorting
  const selectedSorting = productFilteringState.sortCriteria?.sortItems?.find(
    (s) => s.selected
  );

  if (selectedSorting && selectedSorting.query) {
    searchParams.append('sort_by', selectedSorting.sortBy);
    searchParams.append('sort_direction', selectedSorting.sortDirection);
  }

  return searchParams.toString();
};

export const onOptionChanged = (filterId, optionId) => (dispatch, getState) => {
  const filters = [...getState().productFiltering.filters]; // create copy of filters list to not modify the state
  const filterIndex = filters.findIndex((f) => f.id == filterId);
  if (filterIndex == -1) return;

  const { singleSelect, selectedOptions } = filters[filterIndex];
  const optionIndex = selectedOptions.indexOf(optionId);
  const isOptionSelected = optionIndex != -1;
  let updatedSelectedOptions = [];

  if (singleSelect) {
    updatedSelectedOptions = isOptionSelected ? [optionId] : [];
  } else {
    updatedSelectedOptions = isOptionSelected
      ? [
          // selected, remove it from the list
          ...selectedOptions.slice(0, optionIndex),
          ...selectedOptions.slice(optionIndex + 1),
        ]
      : [...selectedOptions, optionId]; // Not selected, add it to selected list
  }
  filters[filterIndex].selectedOptions = updatedSelectedOptions;
  dispatch(updateProductFilteringFilters(filters));
};

export const onRangeChanged = (filterId, values) => (dispatch, getState) => {
  const filters = [...getState().productFiltering.filters]; // create copy of filters list to not modify the state
  const filterIndex = filters.findIndex((f) => f.id == filterId);
  if (
    filterIndex == -1 ||
    filters[filterIndex].filterGroupType != FilterGroupType.Range
  )
    return;

  filters[filterIndex].selectedOptions = values;
  dispatch(updateProductFilteringFilters(filters));
};

const updateProductFilteringFilters = (filters) => ({
  type: PRODUCT_FILTERING_UPDATE_FILTERS,
  payload: filters,
});

export const onSortItemSelected = (sortItem) => (dispatch) => {
  dispatch({
    type: PRODUCT_FILTERING_SET_SORTING,
    payload: sortItem,
  });
  dispatch(queryProductFiltering());
};

export const clearAllFilters = () => (dispatch, getState) => {
  const productFilteringState = getState().productFiltering;
  const filtersCopy = Object.assign([], productFilteringState.filters);

  for (let filter of filtersCopy) {
    filter.selectedOptions = [];

    if (filter.filterGroupType == 'Range') {
      filter.options = filter.options.slice(0, 2);
    }
  }

  dispatch(
    queryProductFiltering({ ...productFilteringState, filters: filtersCopy })
  );
};

export const clearFilterGroup = (filterGroupId) => (dispatch, getState) => {
  const productFilteringState = getState().productFiltering;
  const filtersCopy = Object.assign([], productFilteringState.filters);
  const filterGroup = filtersCopy.find((f) => f.id == filterGroupId);
  if (!filterGroup) return;

  filterGroup.selectedOptions = [];

  if (filterGroup.filterGroupType == 'Range') {
    filterGroup.options = filterGroup.options.slice(0, 2);
    filterGroup.selectedOptions = filterGroup.options.slice(0, 2);
  }

  dispatch(
    queryProductFiltering({ ...productFilteringState, filters: filtersCopy })
  );
};
